import { idConditions, tagConditions, triggerTranslate } from '@src/application/modules/tag/shared/tag/tagConstants';
import { type TFunction } from 'i18next';
import { toIP } from './helpers';

export const targetingToText = (
  name: string,
  include,
  {
    from,
    to,
    range,
    lang,
    value,
    code_country,
    code_city,
    code_least_specific_subdivision,
    code_most_specific_subdivision,
    value_max,
    value_min,
    type_source,
    conditionName,
    condition,
    count,
    datalayer_key,
    feedback,
    grade,
    secondary_grade,
    campaign_id,
    variation_id,
    new_visitor,
    segment,
    min_day,
    max_day,
    durationCondition,
    db_name,
    segment_name,
    action,
    category,
    value_custom,
    version,
  },
  t: TFunction
) => {
  const includeTranslate = {
    true: t('Is'),
    false: t('Is not'),
  };
  switch (name) {
    /* 18 */
    case 'IP': {
      if (range) {
        return {
          condition: includeTranslate[include],
          value: t('in range from {{ from }} to {{ to }}', { from: toIP(from), to: toIP(to) }),
        };
      }
      return { condition: includeTranslate[include], value: toIP(from) };
    }

    /* 19 */
    case 'GEOLOCALISATION': {
      return {
        condition: includeTranslate[include],
        value: [code_country, code_least_specific_subdivision, code_most_specific_subdivision, code_city]
          .filter(Boolean)
          .join(' / '),
      };
    }

    /* 20 */
    case 'COOKIE': {
      return {
        condition: includeTranslate[include],
        value: `"${conditionName}" = ${value}`,
      };
    }

    /* 21 */
    case 'BROWSER_LANGUAGE':
      return { condition: includeTranslate[include], value: lang };

    /* 22 */
    case 'LANDING_PAGE':
    /* 26 */
    case 'PREVIOUS_PAGE': {
      return {
        condition: tagConditions[condition],
        value,
      };
    }

    /* 23 */
    case 'BROWSER': {
      const browserCustomValue = value_custom ? `${value_custom} ${version ? `V${version}` : ''}` : '';
      return {
        condition: includeTranslate[include],
        value: value_custom ? browserCustomValue : `${triggerTranslate.BROWSER[value]} ${version ? `V${version}` : ''}`,
      };
    }

    /* 24 */
    case 'RETURNING_VISITOR':
      return {
        condition: new_visitor ? t('Is new') : t('Is returning'),
        value,
      };

    /* 27 */
    case 'SCREEN_SIZE':
      return { condition: includeTranslate[include], value: `${value_min}px to ${value_max}px` };

    /* 39 */
    case 'SOURCE_TYPE':
      return { condition: includeTranslate[include], value: triggerTranslate.SOURCE_TYPE[type_source] };

    /* 28 */
    case 'JS_VARIABLE':
      return {
        condition: tagConditions[condition],
        value: `Name: ${conditionName}.${value ? ` Value: ${value}.` : ''}`,
      };

    /* 29 */
    case 'CAMPAIGN_EXPOSITION': {
      const campaignExposureCondition = includeTranslate[include];
      return {
        condition: t('{{campaignExposureCondition}} exposed to', { campaignExposureCondition }),
        value:
          t(`Campaign {{campaign_id}}`, { campaign_id }) + variation_id
            ? t(' variation {{variation_id}}', { variation_id })
            : '',
      };
    }

    /* 30 */
    case 'SOURCE': {
      return {
        condition: include ? t('Contains') : t('Does not contain'),
        value: value,
      };
    }

    /* 31 */
    case 'NUMBER_PAGES_VIEWED': {
      return {
        condition: include ? t('Is equal or greater than') : t('Is equal or lower than'),
        value: value,
      };
    }

    /* 33 */
    case 'ADBLOCK':
      return { condition: includeTranslate[include], value: t('enabled') };

    /* 34 */
    case 'SESSION_NUMBER': {
      const sessionNumberConditions = {
        21: { condition: t('Lower than '), value },
        20: { condition: t('Greater than'), value },
        1: { condition: t('Equal'), value },
      };
      return sessionNumberConditions[condition];
    }

    /* 35 */
    case 'WEATHER': {
      // Clear AND from -10°C (-20°F) to 20°C (0°F)
      const convertIntoFahrenheit = (value: number) => Math.round(value * (9 / 5) + 32);
      const valueMin = value_min
        ? t(' AND from {{value_min}}°C ({{value_min_fahrenheit}}°F)', {
            value_min,
            value_min_fahrenheit: convertIntoFahrenheit(value_min),
          })
        : '';
      const valueMax = value_max
        ? t(' to {{value_max}}°C ({{value_max_fahrenheit}}°F)', {
            value_max,
            value_max_fahrenheit: convertIntoFahrenheit(value_max),
          })
        : '';
      const weatherCondition = triggerTranslate.WEATHER[condition];
      return {
        condition: includeTranslate[include],
        value: weatherCondition + valueMin + valueMax,
      };
    }

    /* 36 */
    case 'DAYS_SINCE_LAST_SESSION': {
      return {
        condition: include ? t('is equal or greater than') : t('is equal or lower than'),
        value: t('{{value}} days', { value }),
      };
    }

    /* 39 */
    case 'URL_PARAMETER':
      return { condition: includeTranslate[include], value: `Name: ${conditionName}. Value: ${value}.` };

    /* 41 */
    case 'CUSTOM_VARIABLE': {
      return {
        condition: include ? t('match') : t('does not match'),
        value: `${category} = ${action}`,
      };
    }

    /* 43 */
    case 'SELECTOR': {
      const selectorValue = {
        43: t('ID = "{{value}}"', { value }),
        44: t('Class = "{{value}}"', { value }),
        45: t('Element = "{{value}}"', { value }),
      };
      return {
        condition: include ? t('Selector is') : t('Selector is not'),
        value: selectorValue[condition],
      };
    }

    /* 44 */
    case 'DATALAYER': {
      const oneValue = value.length === 1;
      return {
        condition: tagConditions[condition],
        value: `Key: ${datalayer_key}. Value${!oneValue ? 's' : ''}: ${value.join(' or ')}.`,
      };
    }

    /* 45 */
    case 'LAST_PURCHASE': {
      const lastPurchaseCondition = {
        21: { condition: t('Lower than '), value: t('{{min_day}} days', { min_day }) },
        20: { condition: t('Greater than'), value: t('{{min_day}} days', { min_day }) },
        24: { condition: t('Is between'), value: t('{{min_day}} and {{max_day}} days', { min_day, max_day }) },
      };
      return lastPurchaseCondition[condition];
    }

    /* 46 */
    case 'PURCHASE_FREQUENCY': {
      const purchaseFrequencyCondition = {
        20: t('More than {{value}} times', { value }),
        21: t('Lower than {{value}} times', { value }),
      };
      const purchaseFrequencyValue = {
        20: t('beyond {{min_day}} days', { min_day }),
        21: t('within {{min_day}} days', { min_day }),
        24: t('is between {{min_day}} and {{max_day}} days', { min_day, max_day }),
      };
      return { condition: purchaseFrequencyCondition[condition], value: purchaseFrequencyValue[durationCondition] };
    }

    /* 49 */
    case 'ENGAGEMENT_LEVEL':
      const engagementLevel = {
        1: t('loyal'),
        2: t('valuable'),
        3: t('wanderer'),
        4: t('disengaged'),
      };
      return {
        condition: t('Visitor is'),
        value: engagementLevel[segment],
      };

    /* 51 */
    case 'PAGE_VIEW':
      if (condition === idConditions.savedPages) {
        return {
          condition: tagConditions[include],
          value: t('{{count}} times the condition(s) of {{tagCondition}}', {
            count,
            tagCondition: tagConditions[condition],
          }),
        };
      }
      return {
        condition: tagConditions[condition],
        value: t('{{count}} times the value: {{value}}.', {
          count,
          value,
        }),
      };

    /* 52 */
    case 'PAGE_INTEREST':
      return { condition: tagConditions[condition], value };

    /* 53 */
    case 'PROVIDERS': {
      if (db_name === 'dotaki') {
        const providerCondition = includeTranslate[include];
        return {
          condition: t('Emotion  {{providerCondition}}', { providerCondition }),
          value: segment_name,
        };
      }
      return {
        condition: `${db_name} / ${includeTranslate[include]}`,
        value: value || segment_name,
      };
    }
    /* 54 */
    case 'ABANDONED_CART':
      return {
        condition: include ? t('Has abandoned cart') : t('Has not abandoned cart'),
      };

    /* 55 */
    case 'CSAT':
      return {
        condition: t('Feedback is {{feedback}}', { feedback }),
        value: t('based on {{campaign_id}}', { campaign_id }),
      };

    /* 56 */
    case 'NPS':
      return {
        condition,
        value: secondary_grade
          ? t('{{grade}} to {{secondary_grade}} (based on {{campaign_id}})', {
              grade,
              secondary_grade,
              campaign_id,
            })
          : t('{{grade}} (based on {{campaign_id}})', {
              grade,
              campaign_id,
            }),
      };
    default:
      break;
  }
  return triggerTranslate[name]
    ? { condition: includeTranslate[include], value: triggerTranslate[name][value] }
    : { condition: includeTranslate[include], value };
};
